import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  Input,
} from '@angular/core';
import { BreadcrumbsWrapper } from '@boels/models';
import { RouterStateFacade } from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { GenericLinkComponent } from '../generic-link/generic-link.component';
import { Router } from '@angular/router';
import { combineLatest, first } from 'rxjs';
import { take } from 'rxjs/operators';
import { VerticalAlignerComponent } from '../vertical-aligner/vertical-aligner.component';
import { QaHooksDirective } from '@boels/helpers';

@Component({
  imports: [
    CommonModule,
    FontAwesomeModule,
    GenericLinkComponent,
    TranslocoModule,
    VerticalAlignerComponent,
    QaHooksDirective,
  ],
  selector: 'boels-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  providers: [RouterStateFacade],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbComponent {
  @Input()
  breadcrumbData: BreadcrumbsWrapper;

  #translocoService = inject(TranslocoService);
  #location = inject(Location);
  #router = inject(Router);
  #routerStateFacade = inject(RouterStateFacade);

  faChevronRight = faChevronRight;
  faChevronLeft = faChevronLeft;

  @HostBinding('class.background--white') @Input() whiteBackground = false;

  // Subscription to get common.back content,
  // it is retrieved with a subscription due to loading issues when the json is not YET available
  // @TODO: Check if this can be removed/changed in DOTCOM-7543
  $backTextSub = this.#translocoService
    .selectTranslate('common.back')
    .pipe(take(1));

  goBack(): void {
    combineLatest([
      this.#routerStateFacade.getUrl(),
      this.#routerStateFacade.getNavigationId(),
    ])
      .pipe(first())
      .subscribe(([url, id]: [string, number]) => {
        const region = this.extractRegionFromUrl(url);
        if (id > 2) {
          this.#location.back();
        } else if (region) {
          this.#router.navigate([`branches/${region}`]);
        } else {
          this.#router.navigate([`branches/find`]);
        }
      });
  }

  private extractRegionFromUrl(text: string): string | undefined {
    const regex = /\/branches\/([A-Za-z-]+)\/[A-Za-z-]+/;
    const matches = text.match(regex);
    return matches && matches.length > 1 ? matches[1] : undefined;
  }
}
